/** Canonical site origin — always www. */
export const SITE_URL = 'https://www.databiqs.com';
export const SITE_NAME = 'Databiqs';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`;
export const ORG_LOGO_URL = `${SITE_URL}/search-favicon-192x192.png`;

export const SOCIAL_SAME_AS = [
  'https://www.linkedin.com/company/databiqs',
  'https://www.instagram.com/databiqs/',
];

/** Absolute URL for a path (`/services` → `https://www.databiqs.com/services`). */
export function absoluteUrl(path = '/') {
  if (!path || path === '/') return `${SITE_URL}/`;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
}

/**
 * Per-route SEO defaults (title ≤ ~60 chars, description ~140–160).
 * Used by PageSEO on each page.
 */
export const PAGE_SEO = {
  home: {
    title: 'Enterprise AI Solutions & Automation | Databiqs',
    description:
      'Databiqs delivers enterprise AI—chatbots, automation, machine learning, and consulting—built for production scale, clear ROI, and lasting team adoption.',
    path: '/',
    ogType: 'website',
  },
  services: {
    title: 'Enterprise AI Services | Chatbots & ML | Databiqs',
    description:
      'Explore Databiqs AI services: chatbots, intelligent automation, machine learning, and consulting roadmaps that help enterprise teams ship measurable results.',
    path: '/services',
  },
  'services/ai-chatbots': {
    title: 'AI Chatbots & Conversational AI | Databiqs',
    description:
      'Build enterprise AI chatbots with CRM integration, omnichannel support, and guardrails. Cut response time and convert more high-intent customer conversations.',
    path: '/services/ai-chatbots',
  },
  'services/ai-automation': {
    title: 'AI Automation & Workflow Orchestration | Databiqs',
    description:
      'Automate complex operations with AI workflow orchestration, document intelligence, and integrations designed for reliability and scale across the enterprise.',
    path: '/services/ai-automation',
  },
  'services/machine-learning': {
    title: 'Machine Learning & Predictive Systems | Databiqs',
    description:
      'Production machine learning for forecasting, risk, and real-time scoring—built with validation, drift monitoring, and business adoption from day one.',
    path: '/services/machine-learning',
  },
  'services/ai-consulting': {
    title: 'AI Consulting & Strategy Roadmaps | Databiqs',
    description:
      'Prioritize high-ROI AI use cases with Databiqs consulting: readiness assessments, architecture, governance, and phased roadmaps leaders can fund and run.',
    path: '/services/ai-consulting',
  },
  caseStudies: {
    title: 'Enterprise AI Case Studies & ROI | Databiqs',
    description:
      'See how Databiqs ships production AI for support, automation, speech, forecasting, EdTech, and HealthTech—with measurable latency, ROI, and real adoption.',
    path: '/case-studies',
  },
  'case-studies/novatech': {
    title: 'Enterprise AI Chatbot Case Study | Databiqs',
    description:
      'How Databiqs rebuilt enterprise support with conversational AI triage—78% faster first response, higher CSAT, and $1.2M annual savings without more headcount.',
    path: '/case-studies/novatech',
  },
  'case-studies/workflow-14': {
    title: 'AI Workflow Automation Case Study | Databiqs',
    description:
      'Intelligent workflow orchestration across 14 departments cut manual handoffs and cost—see the AI automation results Databiqs delivered in production.',
    path: '/case-studies/workflow-14',
  },
  'case-studies/voice-transcription-app': {
    title: 'AI Speech-to-Text Case Study | Databiqs',
    description:
      'Production speech recognition and voice-to-text for field teams—accurate transcripts at scale with low latency, search, and enterprise-grade security.',
    path: '/case-studies/voice-transcription-app',
  },
  'case-studies/sentiment-investment-prediction': {
    title: 'ML Sentiment Analysis Case Study | Databiqs',
    description:
      'Machine learning for sentiment-driven investment signals—how Databiqs combined text and market data to improve forecast quality and decision speed.',
    path: '/case-studies/sentiment-investment-prediction',
  },
  'case-studies/avatar-learning-platform': {
    title: 'EdTech AI Learning Platform Case Study | Databiqs',
    description:
      'EdTech conversational AI for personalized language learning—adaptive tutoring, safer dialogue, and higher engagement for classroom and self-study use.',
    path: '/case-studies/avatar-learning-platform',
  },
  'case-studies/data-automation-platform': {
    title: 'Enterprise Data Automation Case Study | Databiqs',
    description:
      'Enterprise data pipeline automation that cut manual ETL, improved data quality, and sped analytics—how Databiqs turned fragile CSVs into trusted insights.',
    path: '/case-studies/data-automation-platform',
  },
  'case-studies/creative-ai-design-tool': {
    title: 'Generative AI Design Tool Case Study | Databiqs',
    description:
      'Generative AI for brand-safe creative workflows—prompt-to-production visuals that accelerated design while protecting brand consistency and review controls.',
    path: '/case-studies/creative-ai-design-tool',
  },
  'case-studies/wellness-ai-companion': {
    title: 'HealthTech AI Wellness Case Study | Databiqs',
    description:
      'HealthTech AI wellness companion with privacy-first architecture—personalized coaching, biometric context, and measurable outcomes for digital health products.',
    path: '/case-studies/wellness-ai-companion',
  },
  about: {
    title: 'About Databiqs | Enterprise AI Development Company',
    description:
      'Learn about Databiqs—our mission, leadership, and approach to shipping reliable enterprise AI products from strategy and architecture through production scale.',
    path: '/about',
  },
  blog: {
    title: 'Enterprise AI Blog & Insights | Databiqs',
    description:
      'Practical enterprise AI insights on automation, machine learning, chatbots, and digital transformation—frameworks and guides from the Databiqs team.',
    path: '/blog',
  },
  contact: {
    title: 'Contact Us | Enterprise AI Team | Databiqs',
    description:
      'Contact Databiqs to discuss AI chatbots, automation, machine learning, or consulting. Share your goals and we will help map the next practical step.',
    path: '/contact',
  },
  bookConsultation: {
    title: 'Book a Free AI Strategy Consultation | Databiqs',
    description:
      'Schedule a free AI strategy call with Databiqs. Align on use cases, architecture, and a practical roadmap for your next enterprise AI initiative.',
    path: '/book-consultation',
  },
  notFound: {
    title: 'Page Not Found | Databiqs',
    description:
      'The page you requested does not exist. Return to the Databiqs homepage or browse our AI services, case studies, and enterprise AI insights.',
    path: '/404',
    noindex: true,
  },
};

export function getServiceSeo(slug) {
  const key = `services/${slug}`;
  return PAGE_SEO[key] || null;
}

export function getCaseStudySeo(slug) {
  const key = `case-studies/${slug}`;
  return PAGE_SEO[key] || null;
}

export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: ORG_LOGO_URL,
  sameAs: SOCIAL_SAME_AS,
};

export const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
};
